/* ============================================================
   High Rise — Landing pages JS
   Wrapped in an IIFE — no globals leak to window. Subscribes
   to Dawn's pub/sub event bus for cart updates. Posts ATC to
   Shopify's native /cart/add.js. Never touches existing theme
   state — only adds to it.
   ============================================================ */
(() => {
  'use strict';

  // Bail if this page is not one of our landings.
  const root = document.querySelector('[data-hr-landing]');
  if (!root) return;

  // Dawn exposes these on window via constants.js + pubsub.js (loaded
  // by theme.liquid on every page). We never overwrite them — just
  // read with fallbacks so we degrade gracefully if Dawn's load order
  // ever changes.
  const PUB_EVENTS = window.PUB_SUB_EVENTS || {
    cartUpdate: 'cart-update',
    cartError:  'cart-error',
  };
  const pub = (name, data) => {
    if (typeof window.publish === 'function') window.publish(name, data);
  };

  const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  const cartUrl    = (window.routes && window.routes.cart_url)    || '/cart';
  const cartGetUrl = '/cart.js';

  /* ----------------------------------------------------------
     Toast — surface success and error messages inline.
     One toast at a time. Auto-dismisses after 3s.
     ---------------------------------------------------------- */
  const toastEl = (() => {
    const el = document.createElement('div');
    el.className = 'hr-landing__toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    document.body.appendChild(el);
    return el;
  })();
  let toastTimer;
  const showToast = (msg, kind) => {
    clearTimeout(toastTimer);
    toastEl.textContent = msg;
    toastEl.classList.remove('hr-landing__toast--error', 'hr-landing__toast--success');
    if (kind) toastEl.classList.add(`hr-landing__toast--${kind}`);
    toastEl.classList.add('is-active');
    toastTimer = setTimeout(() => {
      toastEl.classList.remove('is-active');
    }, 3000);
  };

  /* ----------------------------------------------------------
     ATC — the only commerce operation we perform. Always posts
     to /cart/add.js on the SAME origin. The browser sends the
     visitor's cart cookie automatically; Shopify validates the
     variant + inventory + price LIVE and returns the new line
     item. We don't trust our local prices — Shopify is truth.
     ---------------------------------------------------------- */
  const addToCart = async ({ variantId, quantity, sellingPlanId, properties }) => {
    if (!variantId) throw new Error('Missing variantId');
    const items = [{
      id: Number(variantId),
      quantity: Math.max(1, Number(quantity) || 1),
    }];
    if (sellingPlanId) items[0].selling_plan = Number(sellingPlanId);
    if (properties && typeof properties === 'object') items[0].properties = properties;

    const res = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/javascript',
      },
      credentials: 'same-origin',           // ensures cart cookie is sent
      body: JSON.stringify({ items }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      pub(PUB_EVENTS.cartError, {
        source: 'hr-landing',
        productVariantId: variantId,
        errors: data.errors || data.description,
        message: data.message,
      });
      const err = new Error(data.description || data.message || 'Could not add to cart');
      err.shopifyData = data;
      err.status = res.status;
      throw err;
    }

    // Tell Dawn's cart drawer + cart bubble to refresh themselves.
    pub(PUB_EVENTS.cartUpdate, {
      source: 'hr-landing',
      productVariantId: variantId,
      cartData: data,
    });

    return data;
  };

  /* ----------------------------------------------------------
     Cart count fetch — refreshes the floating FAB badge from
     authoritative Shopify state. Called on:
       - cart-update event (after our own ATC or any other)
       - window 'focus' (when user switches back from another tab)
     ---------------------------------------------------------- */
  const fabCountEl = document.querySelector('.hr-cart-fab__count');
  const updateCartCount = (count) => {
    if (!fabCountEl) return;
    const n = Number(count);
    if (n > 0) {
      fabCountEl.textContent = String(n);
      fabCountEl.hidden = false;
    } else {
      fabCountEl.hidden = true;
    }
  };
  const refreshCartCount = async () => {
    try {
      const res = await fetch(cartGetUrl, { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();
      updateCartCount(cart.item_count);
    } catch (e) {
      // Fail silently — we'd rather show a stale count than throw.
    }
  };

  // Subscribe to Dawn's pubsub so any cart change (ours or theirs)
  // updates our badge automatically. subscribe is on window via pubsub.js.
  if (typeof window.subscribe === 'function') {
    window.subscribe(PUB_EVENTS.cartUpdate, (event) => {
      const newCount = event?.cartData?.item_count;
      if (typeof newCount === 'number') {
        updateCartCount(newCount);
      } else {
        // No item_count in payload — fetch authoritative state.
        refreshCartCount();
      }
    });
  }

  // Refresh when the tab regains focus (someone added in another tab).
  window.addEventListener('focus', refreshCartCount);
  // Also refresh on pageshow for back/forward-cached pages.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) refreshCartCount();
  });

  /* ----------------------------------------------------------
     Wire FAB clicks — open Dawn's cart drawer if present;
     otherwise navigate to /cart.
     ---------------------------------------------------------- */
  const fabEl = document.querySelector('.hr-cart-fab');
  if (fabEl) {
    fabEl.addEventListener('click', (e) => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        e.preventDefault();
        drawer.open(fabEl);     // Dawn's drawer.open() takes the trigger element
      }
      // else: default <a href="/cart"> behavior kicks in
    });
  }

  /* ----------------------------------------------------------
     Generic ATC button wiring — any [data-hr-atc] inside our
     landing scopes invokes addToCart with its data attributes.

     Markup contract:
       <button class="hr-landing__btn"
               data-hr-atc
               data-variant-id="12345"
               data-quantity="1"
               data-selling-plan-id="6789"   (optional, for subscriptions)
       >
         <span class="hr-landing__btn-label">Add to Cart</span>
         <span class="hr-landing__spinner" aria-hidden="true"></span>
       </button>

     Double-click protection: button is disabled + aria-busy
     during the fetch. Network errors surface as a toast.
     ---------------------------------------------------------- */
  root.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-hr-atc]');
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    e.preventDefault();

    const variantId = btn.dataset.variantId;
    if (!variantId) {
      showToast('This item is not available right now.', 'error');
      return;
    }

    btn.setAttribute('aria-busy', 'true');
    btn.setAttribute('disabled', '');

    try {
      await addToCart({
        variantId,
        quantity: btn.dataset.quantity,
        sellingPlanId: btn.dataset.sellingPlanId,
      });
      showToast('Added to cart', 'success');

      // Auto-open the cart drawer so the user sees what they just added.
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        setTimeout(() => drawer.open(btn), 280);
      }
    } catch (err) {
      const msg = err.message || 'Could not add to cart';
      showToast(msg, 'error');
    } finally {
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('disabled');
    }
  });

  // Expose a tiny API on the root element only — for inline scripts
  // in templates that need it (e.g. variety PDP state machine).
  // Still no window globals — only attached to our root element.
  root.__hr = {
    addToCart,
    refreshCartCount,
    showToast,
  };
})();
